import { Outlet, createRootRoute, useLocation } from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import Plausible from "plausible-tracker";
import { useContext, useEffect } from "react";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";

import { isSuperAdminRole } from "~/access/roleRung";
import { useAccess } from "~/access/useAccess";
import { zitadelOrgAtom } from "~/atoms";
import PageContainer from "~/components/PageContainer";
import { useOrgs } from "~/hooks";
import NotFound from "~/pages/NotFound";
import { ClientContext } from "~/utils/Contexts";
import { Env } from "~/utils/Env";
import PageId from "~/utils/PageId";

const trackingId = Env.VITE_GOOGLE_ANALYTICS_TRACKING_ID;

// Skip GA init for local dev (uses DUMMY tracking ID)
if (!trackingId.includes("DUMMY")) {
  ReactGA.initialize(trackingId);
}

const plausible = Plausible({
  domain: "meqinsights.com",
  trackLocalhost: false,
});

const AnalyticsTracker = () => {
  const location = useLocation();
  const zitadelOrg = useAtomValue(zitadelOrgAtom);
  const { currentOrg, getOrgShortName } = useOrgs();
  const { role, hasPlace, payload, error } = useAccess();
  const isSuperAdmin = isSuperAdminRole(role);
  const currentRoleId = role?.id ?? "Not Set";
  // The role reads as absent until the payload lands; tracking before then
  // counts a super admin's page, and counts the page again once it arrives.
  const isRoleSettled = !hasPlace || payload !== undefined || error !== null;

  const currentOrgIdString = currentOrg?.idString;
  const zitadelOrgIdString = zitadelOrg?.idString;

  useEffect(() => {
    if (!isRoleSettled || isSuperAdmin) return;
    plausible.trackPageview(
      {},
      {
        props: {
          env: Env.VITE_ENV,
          currentOrgIdString: currentOrgIdString ?? "Not Set",
          currentRole: currentRoleId,
          orgShortName: getOrgShortName(),
          zitadelOrgIdString: zitadelOrgIdString ?? "Not Set",
        },
      },
    );
  }, [
    currentOrgIdString,
    currentRoleId,
    getOrgShortName,
    isRoleSettled,
    isSuperAdmin,
    location,
    zitadelOrgIdString,
  ]);

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: location.pathname + location.searchStr,
    });
  }, [location]);

  useEffect(() => {
    ReactGA.gtag("set", "user_properties", {
      currentOrgIdString,
      currentRole: currentRoleId,
      zitadelOrgIdString,
    });
  }, [currentOrgIdString, currentRoleId, zitadelOrgIdString]);

  return null;
};

// Keeps the Mantine theme's per-client overrides in sync with the current org
// (formerly an effect inside Routes.tsx's AppRoutes).
const ClientOverridesSync = () => {
  const { getOrgShortName } = useOrgs();
  const currentOrgShortName = getOrgShortName();

  const clientOverides = useContext(ClientContext);
  useEffect(() => {
    if (currentOrgShortName) {
      clientOverides.set(currentOrgShortName);
    }
  }, [clientOverides, currentOrgShortName]);

  return null;
};

const NotFoundRoute = () => {
  const { t } = useTranslation("common");
  return (
    <PageContainer title={t("pageTitles.notFound")} hideNav>
      <NotFound />
    </PageContainer>
  );
};

const Root = () => (
  <>
    <AnalyticsTracker />
    <ClientOverridesSync />
    <Outlet />
  </>
);

export const Route = createRootRoute({
  // The root route is only ever the leaf match when nothing else matched
  // (the 404 case), so its staticData mirrors the old `*` NotFound entry.
  staticData: {
    pageId: PageId.NOT_FOUND,
    titleKey: "pageTitles.notFound",
    hideNav: true,
  },
  component: Root,
  notFoundComponent: NotFoundRoute,
});
